import React from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { fetchUsers, updateUserStatus, type User } from '../../services/MasterAdmin/userService';
import UserDetailsModal from '../../components/MasterAdmin/UserDetailsModal';

const UserAnalyticsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { user?: User } | null;
  const [user, setUser] = React.useState<User | null>(state?.user ?? null);
  const [loadingUser, setLoadingUser] = React.useState<boolean>(false);
  const [updatingStatus, setUpdatingStatus] = React.useState<boolean>(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!userId || user) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingUser(true);
        const response = await fetchUsers();
        const matchedUser = (response?.data || []).find((u) => u.userId === userId) || null;
        if (mounted) setUser(matchedUser);
      } catch (error) {
        console.error('Failed to load user by id for analytics page:', error);
      } finally {
        if (mounted) setLoadingUser(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [userId, user]);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  if (!userId) {
    return (
      <div className="p-8">
        <p className="text-sm text-red-600 dark:text-red-400">User id is missing in URL.</p>
      </div>
    );
  }

  if (loadingUser) {
    return (
      <div className="p-8">
        <p className="text-sm text-gray-600 dark:text-gray-300">Loading user details...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          User details not available. Please open this page from the Users list, or go back and try again.
        </p>
        <button
          onClick={() => navigate('/users')}
          className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const handleToggleBlock = async () => {
    if (!user) return;
    try {
      setUpdatingStatus(true);
      setActionError(null);
      const nextStatus = !user.status;
      await updateUserStatus(user.userId, nextStatus);
      setUser((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      setMenuOpen(false);
    } catch (e) {
      console.error('Failed to update user status from analytics page:', e);
      setActionError('Failed to update user status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="w-full min-w-0 p-3 sm:p-4 md:p-6">
      <div className="mx-auto w-full min-w-0 max-w-7xl">
        {actionError && (
          <div className="mb-4 text-sm text-red-600 dark:text-red-400">{actionError}</div>
        )}
        <UserDetailsModal
          user={user}
          isOpen={true}
          onClose={() => navigate('/users')}
          standalone
          headerActions={(
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((prev) => !prev)}
                className="inline-flex rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                title="Actions"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-20 mt-2 w-48 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="border-b border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    Status: <span className="font-semibold">{user.status ? 'Active' : 'Blocked'}</span>
                  </div>
                  <button
                    role="menuitem"
                    onClick={handleToggleBlock}
                    disabled={updatingStatus}
                    className="mt-1 w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-60 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {updatingStatus ? 'Updating...' : user.status ? 'Block User' : 'Unblock User'}
                  </button>
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default UserAnalyticsPage;
